import { BigNumber } from "@yjsgames/bignumber";
import { getLanguage, t } from "../i18n/i18n.js";
import { formatBig } from "./formatNumber.js";
import { RELIC_DEFS_ORDERED, LAUNCHER, MODULE_BRICK_BALANCE } from "../data/balance.js";
import { EconomyController } from "../systems/EconomyController.js";
import { GoldModuleBox } from "../systems/GoldModuleBox.js";
import { ModuleInventory } from "../systems/ModuleInventory.js";
import { ModuleGridController } from "../systems/ModuleGridController.js";
import { ModuleRegistry } from "../modules/ModuleRegistry.js";
import { effectiveRarity, type ModuleInstance } from "../models/ModuleInstance.js";
import type { ModuleDefinition, ModuleRarity } from "../modules/ProjectileModule.js";
import { defaultPieceOf, dedupePieces, type ModulePiece } from "../data/modulePieces.js";
import {
  evaluateModuleEffectStats,
  moduleEffectChance,
  modulePipeCritChance,
  type ModuleEffectStat,
} from "../modules/ModuleEffect.js";
import { nextRarity, RARITY_PROGRESSION, unplacedMergeCandidates } from "../systems/ModuleMerge.js";
import { fusionCandidates } from "../systems/ModuleFusion.js";
import type { ModuleMergeTabData } from "./ModuleMergeTabView.js";
import type { ModuleFusionTabData, FusionCostView } from "./ModuleFusionTabView.js";
import { UpgradeInventory } from "../models/UpgradeInventory.js";
import { ChapterController } from "../systems/ChapterController.js";
import { RebirthController } from "../systems/RebirthController.js";
import { RelicController } from "../systems/RelicController.js";
import { DungeonController } from "../systems/DungeonController.js";
import { DailyShopController } from "../systems/DailyShopController.js";
import type { ModuleGridLane, PlacedModule } from "../models/ModuleGrid.js";
import { dailyShopRewardSpec, dailyShopCostAmount, type DailyShopCurrency } from "../data/dailyShopData.js";
import { SHOP_AD_PRODUCTS } from "../data/shopAdData.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import {
  DAILY_RESET_KEY_DUNGEON_TICKETS,
  DAILY_RESET_KEY_DAILY_SHOP,
} from "../models/ModuleBrickSaveData.js";
import type { RewardSpec } from "../models/RewardSpec.js";
import type { ShopData, ShopAdRowData } from "./ShopTabTypes.js";
import type { RelicData } from "./RelicTabView.js";
import type { DungeonTabData } from "./DungeonTabView.js";
import type { DailyShopTabData, DailyShopSlotData } from "./DailyShopTabView.js";
import type { PaletteRow } from "./ModulePaletteView.js";
import type { InventoryRow } from "./ModuleInventoryView.js";

// 탭 표시 데이터 빌더. 도메인 state(Controller)를 View 데이터로 변환한다.

interface IapShopAvailability {
  readonly removeAds: boolean;
  readonly removeAdsPackage: boolean;
  readonly removeAdsPriceLabel?: string;
  readonly removeAdsPackagePriceLabel?: string;
}

/** 상점 탭 — 골드/다이아 상자 비용·구매 가능·최근 결과 + 광고 제거 IAP(보유/스토어 가격) + 광고 다이아 행. */
export function buildShopData(
  economy: EconomyController,
  goldBox: GoldModuleBox,
  lastBoxResult: string,
  adFree = false,
  cooldown?: CooldownService,
  dailyAdClaimed = false,
  iapAvailability: IapShopAvailability = { removeAds: false, removeAdsPackage: false },
  adProcessing = false,
  rebirth: RebirthController = new RebirthController(),
): ShopData {
  const goldCost = EconomyController.goldBoxCost(goldBox.boxOpenCount);
  const gold10Cost = EconomyController.goldBox10Cost(goldBox.boxOpenCount);
  const diaCost = EconomyController.diamondBoxCost();
  const rebirthResetCost = EconomyController.rebirthConditionResetCost();
  const rebirthInitialRequiredChapter = rebirth.initialRequiredChapter();
  return {
    goldBoxCost: goldCost,
    goldBoxAffordable: economy.gold.gte(goldCost),
    goldBox10Cost: gold10Cost,
    goldBox10Affordable: economy.gold.gte(gold10Cost),
    diamondBoxCost: diaCost,
    diamondBoxAffordable: economy.diamond.gte(diaCost),
    rebirthConditionResetCost: rebirthResetCost,
    rebirthConditionResetAffordable: economy.diamond.gte(rebirthResetCost),
    rebirthConditionResetAvailable: rebirth.requiredChapter() > rebirthInitialRequiredChapter,
    rebirthInitialRequiredChapter,
    resultLabel: lastBoxResult,
    adFree,
    removeAdsAvailable: iapAvailability.removeAds,
    removeAdsPackageAvailable: iapAvailability.removeAdsPackage,
    removeAdsPriceLabel: iapAvailability.removeAdsPriceLabel,
    removeAdsPackagePriceLabel: iapAvailability.removeAdsPackagePriceLabel,
    adRows: buildShopAdRows(cooldown, dailyAdClaimed, adProcessing),
  };
}

// 광고 다이아 행 데이터 — cooldown 미주입(테스트/부팅 전)이면 remainMs=0으로 폴백.
function buildShopAdRows(
  cooldown: CooldownService | undefined,
  dailyAdClaimed: boolean,
  adProcessing: boolean,
): readonly ShopAdRowData[] {
  return SHOP_AD_PRODUCTS.map((p) => {
    const remainMs = p.gateKind === "cooldown" ? (cooldown?.remainMs(p.id) ?? 0) : 0;
    const gated = p.gateKind === "cooldown" ? remainMs > 0 : dailyAdClaimed;
    return {
      id: p.id,
      descKey: p.descKey,
      rewardAmount: p.rewardAmount,
      iconKey: p.iconKey,
      gateKind: p.gateKind,
      remainMs,
      gated,
      processing: adProcessing && !gated,
    };
  });
}

/** 모듈 팔레트 행 — 보유 인스턴스(이름/레벨/등급/배치 여부). level은 moduleId별 플레이어 자산(levelOf). */
export function buildPaletteRows(
  inventory: ModuleInventory,
  grid: ModuleGridController,
  registry: ModuleRegistry,
  levelOf: (moduleId: string) => number,
): PaletteRow[] {
  const placed = new Set(grid.placedInstanceIds());
  return inventory.allInstances().map((inst) => {
    const def = registry.getDefinition(inst.definitionId);
    return {
      instanceId: inst.instanceId,
      definitionId: inst.definitionId,
      name: t(`module.${inst.definitionId}.name`),
      level: levelOf(inst.definitionId),
      rarity: effectiveRarity(inst, def),
      placed: placed.has(inst.instanceId),
      shapeId: inst.shapeId,
      footprintId: inst.footprintId,
    };
  });
}

type PaletteBaseRow = Omit<PaletteRow, "placed">;

export interface PaletteRowsCache {
  inventoryRevision: number;
  levelRevision: number;
  language: string;
  gridRevision: number;
  baseRows: PaletteBaseRow[];
  rows: PaletteRow[];
}

export function createPaletteRowsCache(): PaletteRowsCache {
  return {
    inventoryRevision: -1,
    levelRevision: -1,
    language: "",
    gridRevision: -1,
    baseRows: [],
    rows: [],
  };
}

function buildPaletteBaseRows(
  inventory: ModuleInventory,
  registry: ModuleRegistry,
  levelOf: (moduleId: string) => number,
): PaletteBaseRow[] {
  return inventory.allInstances().map((inst) => {
    const def = registry.getDefinition(inst.definitionId);
    return {
      instanceId: inst.instanceId,
      definitionId: inst.definitionId,
      name: t(`module.${inst.definitionId}.name`),
      level: levelOf(inst.definitionId),
      rarity: effectiveRarity(inst, def),
      shapeId: inst.shapeId,
      footprintId: inst.footprintId,
    };
  });
}

/**
 * 전투 탭 팔레트 전용 캐시. 인벤토리/레벨/언어가 같으면 이름·등급·레벨 계산을 재사용하고,
 * 장착/해제 시에는 placed boolean이 바뀐 row만 새 객체로 바꿔 visible row diff 비용을 줄인다.
 */
export function buildPaletteRowsCached(
  cache: PaletteRowsCache,
  inventory: ModuleInventory,
  grid: ModuleGridController,
  registry: ModuleRegistry,
  levelOf: (moduleId: string) => number,
  levelRevision = 0,
): readonly PaletteRow[] {
  const language = getLanguage();
  if (
    cache.inventoryRevision !== inventory.revision ||
    cache.levelRevision !== levelRevision ||
    cache.language !== language
  ) {
    cache.inventoryRevision = inventory.revision;
    cache.levelRevision = levelRevision;
    cache.language = language;
    cache.gridRevision = -1;
    cache.baseRows = buildPaletteBaseRows(inventory, registry, levelOf);
    cache.rows = [];
  }

  if (cache.gridRevision === grid.revision) return cache.rows;

  const placed = new Set(grid.placedInstanceIds());
  const nextRows: PaletteRow[] = new Array(cache.baseRows.length);
  for (let i = 0; i < cache.baseRows.length; i++) {
    const base = cache.baseRows[i]!;
    const isPlaced = placed.has(base.instanceId);
    const prev = cache.rows[i];
    nextRows[i] = prev && prev.placed === isPlaced ? prev : { ...base, placed: isPlaced };
  }
  cache.gridRevision = grid.revision;
  cache.rows = nextRows;
  return cache.rows;
}

export function adjustPaletteRowsForPage(
  rows: readonly PaletteRow[],
  placedModules: readonly PlacedModule[],
  gridHeight: number,
  lanes: readonly ModuleGridLane[] | undefined,
  pageIndex: number,
  visibleRows: number,
): PaletteRow[] {
  if (!lanes || lanes.length <= 1) return [...rows];
  const pageStartY = Math.max(0, pageIndex) * visibleRows;
  const pageEndY = Math.min(gridHeight, pageStartY + visibleRows);
  const visiblePlacedIds = new Set(
    placedModules
      .filter((module) => module.y >= pageStartY && module.y < pageEndY)
      .map((module) => module.instanceId),
  );
  return rows.map((row) => {
    const placed = visiblePlacedIds.has(row.instanceId);
    return row.placed === placed ? row : { ...row, placed };
  });
}

/**
 * 모듈 인벤토리 행 — 팔레트 행 + 강화 가능 여부/강화 비용 힌트 (강화석 전용).
 * level/강화는 moduleId별 플레이어 자산이라 같은 moduleId 인스턴스 행은 동일 level·canLevelUp을 보인다.
 */
export function buildInventoryRows(
  inventory: ModuleInventory,
  grid: ModuleGridController,
  registry: ModuleRegistry,
  upgradeInv: UpgradeInventory,
  levelOf: (moduleId: string) => number,
): InventoryRow[] {
  const placed = new Set(grid.placedInstanceIds());
  return inventory.allInstances().map((inst) => {
    const def = registry.getDefinition(inst.definitionId);
    const level = levelOf(inst.definitionId);
    const stoneCost = EconomyController.upgradeStoneCost(level);
    const canLevelUp = upgradeInv.universalUpgradeStone >= stoneCost;
    return {
      instanceId: inst.instanceId,
      definitionId: inst.definitionId,
      name: t(`module.${inst.definitionId}.name`),
      level,
      rarity: effectiveRarity(inst, def),
      placed: placed.has(inst.instanceId),
      canLevelUp,
      levelUpHint: t("inventory.levelUpHint", { stone: stoneCost }),
    };
  });
}

/**
 * 모듈 합성 탭 — 미배치 보유 모듈을 effectiveRarity까지 반영해 전달 (전투 그리드 배치분은 unplacedMergeCandidates가 제외).
 * 최고 등급(nextRarity가 null = legendary)은 더 합성할 수 없으므로 그리드에서 제외한다 — 합성 가능한 재료만 노출.
 * (legendary 보유 확인은 모듈/인벤토리 탭에서 가능.) rarity는 effectiveRarity 적용값이라 합성 오버라이드도 정확히 반영.
 */
export function buildModuleMergeData(
  inventory: ModuleInventory,
  grid: ModuleGridController,
  registry: ModuleRegistry,
  levelOf: (moduleId: string) => number,
): ModuleMergeTabData {
  return {
    modules: unplacedMergeCandidates(inventory, grid)
      .map((inst) => {
        const def = registry.getDefinition(inst.definitionId);
        return {
          instanceId: inst.instanceId,
          definitionId: inst.definitionId,
          name: t(`module.${inst.definitionId}.name`),
          level: levelOf(inst.definitionId),
          rarity: effectiveRarity(inst, def),
          // 파이프 미리보기용 — 풀 기본 모양 + 슬롯 점유 모양(buildPaletteRows와 동형).
          shapeId: inst.shapeId,
          footprintId: inst.footprintId,
        };
      })
      .filter((row) => nextRarity(row.rarity) !== null),
  };
}

/**
 * 융합 탭 데이터 — 미배치·미융합 후보(fusionCandidates) + 현재 비용(광고/다이아). 합성과 달리 등급 제약(nextRarity 필터) 없음
 * (같은 footprint 2개면 종류/등급 무관). cost는 GameScene이 ModuleFusionController.cost(adFree)로 산출해 주입.
 */
export function buildModuleFusionData(
  inventory: ModuleInventory,
  grid: ModuleGridController,
  registry: ModuleRegistry,
  cost: FusionCostView,
  levelOf: (moduleId: string) => number,
): ModuleFusionTabData {
  return {
    cost,
    modules: fusionCandidates(inventory, grid).map((inst) => {
      const def = registry.getDefinition(inst.definitionId);
      return {
        instanceId: inst.instanceId,
        definitionId: inst.definitionId,
        name: t(`module.${inst.definitionId}.name`),
        level: levelOf(inst.definitionId),
        rarity: effectiveRarity(inst, def),
        // 파이프 미리보기용 — 풀 기본 모양 + 슬롯 점유 모양(buildModuleMergeData와 동형).
        shapeId: inst.shapeId,
        footprintId: inst.footprintId,
      };
    }),
  };
}

/** 모듈 정보 팝업 표시 데이터 (Phaser 비의존 순수). 합성 결과/전투 그리드 탭 공용. */
export interface ModuleInfoData {
  readonly definitionId: string;
  /** 표시 이름 i18n 키. 우주선별 시작 모듈처럼 definitionId와 표시 키가 다를 때 사용한다. */
  readonly nameKey?: string;
  /** 설명 i18n 키. 우주선별 시작 모듈처럼 definitionId와 표시 키가 다를 때 사용한다. */
  readonly descKey?: string;
  /** 대표 아이콘 texture key. 미지정 시 definitionId 기반 모듈 아이콘을 사용한다. */
  readonly iconKey?: string;
  readonly name: string;
  readonly rarity: ModuleRarity;
  readonly level: number;
  /** 기능 설명 (module.<id>.desc). */
  readonly descText: string;
  /** 이 모듈의 조각 풀 — (shapeId, footprintId) 쌍 기준 중복제거 (modules.tsv pieces). shapeId만으로 dedup하면 footprint가 소실돼 모든 조각을 한 칸 크기로 그리게 되므로 쌍으로 보존한다. 팝업이 조각별 footprint로 그린다. */
  readonly piecePool: readonly ModulePiece[];
  /** 이 인스턴스가 고정 보유한 조각의 연결 모양 — 메인 미리보기 렌더용. */
  readonly shapeId: string;
  /** 슬롯 점유 모양(single/vert2/horz2) — 미리보기가 실제 배치 방향과 일치하도록. */
  readonly footprintId: string;
  /** 유효 등급 effect의 발동 확률(0~100 정수). chance 게이트가 있는 모듈만 — 없으면 undefined(표시 생략). */
  readonly chancePercent?: number;
  /** 유효 등급 effect의 파이프 치명타 확률(0~100 정수). pipeCritChance가 있는 모듈만. */
  readonly pipeCritPercent?: number;
  /** 현재 등급·레벨로 평가한 구체 스탯 (라벨 키 + 값). 팝업이 t(moduleStat.<key>)로 조립. 없으면 빈 배열. */
  readonly stats: readonly ModuleEffectStat[];
  /** 미발견(미획득) 모듈 — 도감에서 정의만으로 열 때 true. 팝업이 효과 수치/확률을 ???로 가린다(이름/등급/모양 풀은 공개). */
  readonly locked?: boolean;
  /** 현재 최고 도달 챕터(bestChapter) — 팝업이 조각별 해금 여부(unlockChapter)를 판정해 잠긴 조각에 자물쇠+챕터를 표시. 기본 Infinity. */
  readonly bestChapter: number;
  /**
   * 등급 미리보기 네비게이션 — 이 모듈 정의에 effect가 정의된 등급들의 variant 목록 + 현재 보유 등급 위치.
   * 팝업의 ◀/▶ 버튼이 variants를 순회해 등급별 효과를 미리 보여준다(합성 시 강해지는 정도 비교).
   * variant 각각은 rarityNav 없는 단일 등급 데이터(재귀 방지). 등급이 1개뿐이면 undefined(버튼 숨김).
   */
  readonly rarityNav?: {
    readonly variants: readonly ModuleInfoData[];
    /** 보유(현재) 등급의 variants 내 인덱스 — 팝업이 여기서 시작 + "보유" 강조 기준. */
    readonly index: number;
  };
}

export interface LauncherInfoSource {
  readonly launcherNameKey: string;
  readonly launcherDescKey: string;
  readonly launcherIconKey: string;
}

/**
 * 이 모듈 정의에 effect가 정의된 등급들의 variant 목록 생성(순수) — 등급 미리보기 네비용.
 * 등급이 2개 이상일 때만 반환(1개뿐이면 네비 불필요 → undefined). variants는 RARITY_PROGRESSION(낮음→높음) 순.
 */
function buildRarityVariants(
  effects: Partial<Record<ModuleRarity, unknown>> | undefined,
  currentRarity: ModuleRarity,
  makeForRarity: (rarity: ModuleRarity) => ModuleInfoData,
): { readonly variants: readonly ModuleInfoData[]; readonly index: number } | undefined {
  const rarities = RARITY_PROGRESSION.filter((r) => effects?.[r] != null);
  if (rarities.length <= 1) return undefined;
  return {
    variants: rarities.map(makeForRarity),
    index: Math.max(0, rarities.indexOf(currentRarity)),
  };
}

/** 인스턴스 + 특정 등급 → 단일 등급 정보 데이터(rarityNav 없음). 등급 미리보기 variant 생성용 내부 헬퍼. level은 moduleId별 플레이어 자산. */
function buildInstInfoForRarity(
  inst: ModuleInstance,
  def: ModuleDefinition | undefined,
  rarity: ModuleRarity,
  bestChapter: number,
  level: number,
): ModuleInfoData {
  // 조각 풀 (중복 제거, footprint 보존) — 정보 팝업 "모양" 행 표시용(인스턴스 실제 조각은 shapeId/footprintId).
  const piecePool = dedupePieces(def?.pieces ?? []);
  // 해당 등급 effect의 chance를 평가 → 0~100 정수. chance 게이트 없는 등급이면 undefined(팝업 라인 숨김).
  const effect = def?.effects[rarity];
  const p = moduleEffectChance(effect, level);
  const pipeCrit = modulePipeCritChance(effect, level);
  // 구체 스탯 — 해당 등급·레벨로 평가한 (라벨 키 + 값). 라벨 현지화는 팝업이 t(moduleStat.<key>)로.
  const stats = effect ? evaluateModuleEffectStats(effect, level) : [];
  return {
    definitionId: inst.definitionId,
    name: t(`module.${inst.definitionId}.name`),
    rarity,
    level,
    descText: t(`module.${inst.definitionId}.desc`),
    piecePool,
    shapeId: inst.shapeId,
    footprintId: inst.footprintId,
    stats,
    bestChapter,
    ...(p !== undefined ? { chancePercent: Math.round(p * 100) } : {}),
    ...(pipeCrit !== undefined ? { pipeCritPercent: Math.round(pipeCrit * 100) } : {}),
  };
}

/**
 * 모듈 인스턴스 → 정보 팝업 데이터. 정의 조각 풀의 모양 목록/desc + 인스턴스의 조각(shapeId/footprintId)·레벨·유효등급.
 * 정의에 effect가 여러 등급 정의돼 있으면 rarityNav(등급 미리보기 variants)를 채워 ◀/▶ 네비를 켠다.
 * bestChapter는 잠긴 조각(unlockChapter > bestChapter) 자물쇠+챕터 표시용 — 기본 Infinity(전부 해금).
 */
/**
 * 런처(그리드 시작점) → 정보 팝업 데이터.
 * spaceshipId를 전달하면 spaceship.<id>.launcher.name/desc 키를 사용해
 * 우주선별 고유 런처 정체성을 표시한다. 미전달 시 범용 module.launcher.* 폴백.
 */
export function buildLauncherInfoData(source?: LauncherInfoSource): ModuleInfoData {
  const piece: ModulePiece = { shapeId: LAUNCHER.shapeId, footprintId: LAUNCHER.footprintId };
  const nameKey = source?.launcherNameKey ?? "module.launcher.name";
  const descKey = source?.launcherDescKey ?? "module.launcher.desc";
  const iconKey = source?.launcherIconKey ?? "icon_effect_launcher";
  return {
    definitionId: "launcher",
    nameKey,
    descKey,
    iconKey,
    name: t(nameKey),
    rarity: "common",
    level: 1,
    descText: t(descKey),
    piecePool: [piece],
    shapeId: LAUNCHER.shapeId,
    footprintId: LAUNCHER.footprintId,
    stats: launcherInfoStats(),
    bestChapter: Infinity,
  };
}

function launcherInfoStats(): ModuleEffectStat[] {
  const stats: ModuleEffectStat[] = [
    { key: "baseDamage", value: formatBig(BigNumber.fromNumber(MODULE_BRICK_BALANCE.baseDamage)) },
  ];
  stats.push(...evaluateModuleEffectStats(LAUNCHER.effect, 1).map((s) =>
    s.key === "critChance" ? { ...s, value: s.value.replace(/^\+/, "") } : s,
  ));
  stats.push({ key: "launcherCritDamage", value: `×${MODULE_BRICK_BALANCE.critMultiplier}` });
  return stats;
}

export function buildModuleInfoData(
  inst: ModuleInstance,
  registry: ModuleRegistry,
  bestChapter = Infinity,
  level = 1,
): ModuleInfoData {
  const def = registry.getDefinition(inst.definitionId);
  const rarity = effectiveRarity(inst, def);
  const base = buildInstInfoForRarity(inst, def, rarity, bestChapter, level);
  const nav = buildRarityVariants(def?.effects, rarity, (r) =>
    buildInstInfoForRarity(inst, def, r, bestChapter, level),
  );
  return nav ? { ...base, rarityNav: nav } : base;
}

/**
 * 정의(ModuleDefinition) → 정보 팝업 데이터 (도감 전용, 순수). 인스턴스 없이 정의만으로 구성한다.
 * shapeId/footprintId는 대표 조각(첫 항목), rarity는 정의 등급, level은 moduleId별 플레이어 레벨(환생 유지, 미강화=1).
 * locked=true(미발견)면 효과 수치(stats)/발동 확률(chancePercent)을 생략한다 — 팝업이 ???로 가려 표시.
 * 이름/등급/모양 풀은 발견 여부와 무관하게 공개.
 */
export function buildModuleInfoDataFromDef(
  def: ModuleDefinition,
  level: number,
  locked: boolean,
  bestChapter = Infinity,
): ModuleInfoData {
  const base = buildDefInfoForRarity(def, level, locked, def.rarity, bestChapter);
  const nav = buildRarityVariants(def.effects, def.rarity, (r) =>
    buildDefInfoForRarity(def, level, locked, r, bestChapter),
  );
  return nav ? { ...base, rarityNav: nav } : base;
}

/** 정의 + 특정 등급 → 단일 등급 정보 데이터(rarityNav 없음). 도감 등급 미리보기 variant 생성용 내부 헬퍼. */
function buildDefInfoForRarity(
  def: ModuleDefinition,
  level: number,
  locked: boolean,
  rarity: ModuleRarity,
  bestChapter: number,
): ModuleInfoData {
  const piece = defaultPieceOf(def.pieces);
  const piecePool = dedupePieces(def.pieces);
  const effect = def.effects[rarity];
  // locked면 효과 수치/확률 가림 — 데이터에서 비우고(stats=[]·chancePercent 생략) 팝업이 ???로 표기.
  const stats = locked || !effect ? [] : evaluateModuleEffectStats(effect, level);
  const p = locked ? undefined : moduleEffectChance(effect, level);
  const pipeCrit = locked ? undefined : modulePipeCritChance(effect, level);
  return {
    definitionId: def.id,
    name: t(`module.${def.id}.name`),
    rarity,
    level,
    descText: t(`module.${def.id}.desc`),
    piecePool,
    shapeId: piece.shapeId,
    footprintId: piece.footprintId,
    stats,
    locked,
    bestChapter,
    ...(p !== undefined ? { chancePercent: Math.round(p * 100) } : {}),
    ...(pipeCrit !== undefined ? { pipeCritPercent: Math.round(pipeCrit * 100) } : {}),
  };
}

// 스탯/환생 탭 빌더 — statTabData.ts로 분리, 호출부 호환 위해 re-export.
export { buildRebirthData, buildStatUpgradeData } from "./statTabData.js";

/** 유물 탭 — 전 유물 보유 개수 맵. */
export function buildRelicData(relic: RelicController): RelicData {
  const counts: Record<string, number> = {};
  for (const def of RELIC_DEFS_ORDERED)
    counts[def.relicId] = relic.count(def.relicId);
  return { counts };
}

/** 던전 탭 — 자정 리셋 반영 후 현재 던전/보상/도전권/리셋 남은시간 + 도전·소탕 가능 여부. */
export function buildDungeonData(
  dungeon: DungeonController,
  dailyReset: DailyResetService,
  dungeonMode: boolean,
): DungeonTabData {
  dailyReset.check(); // 자정 통과 시 onReset → 도전권 충전 + 저장
  const index = dungeon.currentIndex;
  return {
    index: index + 1, // 0-based → 1-based 표시
    rewardSummary: formatRewardSummary(DungeonController.rewardForIndex(index)),
    msUntilReset: dailyReset.msUntilNextReset(DAILY_RESET_KEY_DUNGEON_TICKETS),
    ticketsRemaining: dungeon.ticketsRemaining,
    ticketLimit: dungeon.ticketLimit,
    canChallenge: !dungeonMode && dungeon.canChallenge(),
    canSweep: !dungeonMode && dungeon.canSweep(),
  };
}

/** 일일상점 탭 — 자정 리셋 반영 후 6슬롯/리롤/카운트다운/구매결과 (idleblade RandomShop 레퍼런스). */
export function buildDailyShopData(
  dailyShop: DailyShopController,
  dailyReset: DailyResetService,
  economy: EconomyController,
  chapter: ChapterController,
  lastDailyShopResult: string,
  adFree = false,
  adProcessing = false,
): DailyShopTabData {
  dailyReset.check(); // 자정 통과 시 onReset → 슬롯 재추첨 + 저장 (buildDungeonData와 동형)
  const bestChapter = chapter.bestChapter;
  const rerollCost = dailyShop.rerollCost();
  const rerollLabel = !rerollCost
    ? t("dailyShop.refreshSoldOut")
    : rerollCost.kind === "free"
      ? t("dailyShop.refreshFree")
      : t("dailyShop.refreshDia", { cost: rerollCost.amount });
  const rerollAvailable =
    rerollCost !== null &&
    (rerollCost.kind === "free" ||
      economy.diamond.gte(BigNumber.fromNumber(rerollCost.amount)));
  const slots: DailyShopSlotData[] = [];
  for (let i = 0; i < dailyShop.slotCount; i++) {
    const slot = dailyShop.slotAt(i)!;
    const entry = dailyShop.slotEntry(i)!;
    const cost = dailyShopCostAmount(entry, slot.discountPercent, bestChapter);
    const isAdProcessing = entry.costCurrency === "ad" && !adFree && adProcessing && !slot.purchased;
    const affordable =
      entry.costCurrency === "ad"
        ? !isAdProcessing
        : entry.costCurrency === "gold"
          ? economy.gold.gte(cost)
          : economy.diamond.gte(cost);
    const reward = formatRewardSummary([dailyShopRewardSpec(entry, bestChapter)]);
    const costText = dailyShopCostText(entry.costCurrency, cost, adFree);
    // 코스트는 구매 버튼(costLabel), 할인율은 우상단 배지(discountLabel)가 담당 — 설명엔 보상만.
    const detail = t("dailyShop.detail", { reward });
    const discountLabel =
      slot.discountPercent > 0
        ? t("dailyShop.discountBadge", { percent: slot.discountPercent })
        : "";
    const costIconKey =
      entry.costCurrency === "gold"
        ? "icon_gold"
        : entry.costCurrency === "diamond"
          ? "icon_diamond"
          : undefined;
    // 버튼 라벨 = 비용 수량(아이콘이 통화 표시). ad는 아이콘 없이 "광고 시청"/"무료 수령" 텍스트.
    const costLabel = entry.costCurrency === "ad" ? costText : formatBig(cost);
    slots.push({
      name: t(entry.nameKey),
      detail,
      soldOut: slot.purchased,
      affordable,
      iconKey: entry.iconKey,
      costIconKey,
      costLabel,
      adProcessing: isAdProcessing,
      discountLabel,
    });
  }
  return {
    msUntilReset: dailyReset.msUntilNextReset(DAILY_RESET_KEY_DAILY_SHOP),
    rerollLabel,
    rerollAvailable,
    resultLabel: lastDailyShopResult,
    slots,
  };
}

/** 통화 종류 + 금액 현지화 ("{cost} 골드" / "{cost} 다이아"). adFree면 ad 코스트 라벨이 "무료 수령"으로 전환. */
export function dailyShopCostText(
  currency: DailyShopCurrency,
  amount: BigNumber,
  adFree = false,
): string {
  if (currency === "ad") return adFree ? t("dailyShop.costAdFree") : t("dailyShop.costAd");
  return currency === "gold"
    ? t("dailyShop.costGold", { cost: formatBig(amount) })
    : t("dailyShop.costDia", { cost: formatBig(amount) });
}

/** RewardSpec[] → 현지화된 보상 요약 문자열 (던전 탭·일일상점 표시용). */
export function formatRewardSummary(specs: readonly RewardSpec[]): string {
  if (specs.length === 0) return t("reward.none");
  const parts = specs.map((s) => {
    switch (s.kind) {
      case "gold":
        return t("reward.gold", {
          amount: formatBig(BigNumber.fromString(s.amount)),
        });
      case "diamond":
        return t("reward.diamond", {
          amount: formatBig(BigNumber.fromString(s.amount)),
        });
      case "upgradeStone":
        return t("reward.upgradeStone", { amount: s.amount });
      case "rebirthPoint":
        return t("reward.rebirthPoint", { amount: s.amount });
      case "randomModule":
        return t("reward.randomModule");
      case "artifact":
        return t("reward.artifact");
    }
  });
  return parts.join(", ");
}
